import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../../Translation";
import Badge from "../../Badge";
import { ArrowDownIcon, ArrowsPointingOutIcon, ChevronDownIcon, Cog8ToothIcon, DocumentIcon, ExclamationCircleIcon, MinusIcon, PlusIcon, VariableIcon } from "@heroicons/react/24/outline";
import { split, webhook } from "../../../Resources/Images";
import { TextInput } from "../../Inputs/TextInput";
import { TextArea } from "../../Inputs/TextArea";
import { Button } from "../../Buttons";
import Tabs, { useTabs } from "../../Tabs";
import { useAPI } from "../../../API";
import Select from "../../Inputs/Select";
import { bgColor, hoverBgColor, textColor } from "../../../Resources/CSS/styling";


const ConfigurationTab = ({ tmpStep, setTmpStep, save, error, isCreate }) => {

    const { translate } = useTranslation();
    const api = useAPI();


    const sourceOptions = [
        { id: 'event', name: translate('automatisation.from_event') },
        { id: 'contact', name: translate('automatisation.from_contact') },
        { id: 'global', name: translate('automatisation.from_global') }
    ];
   const selectedSource = sourceOptions.find(source => tmpStep.data.variable.startsWith(source.id)) || sourceOptions[0];

   const setSource = (source) => {
    setTmpStep({ ...tmpStep, data: { ...tmpStep.data, variable: source.id + tmpStep.data.variable.split(selectedSource.id)[1] } });
   }

   const setVariable = (variable) => {
    setTmpStep({ ...tmpStep, data: { ...tmpStep.data, variable: selectedSource.id + "." + variable } });
   }

   const variable = tmpStep.data.variable.split(selectedSource.id + ".").slice(1).join(".");

   const number_operations = [
    { id: 'ADDITION', name: translate('automatisation.add') },
    { id: 'SUBTRACTION', name: translate('automatisation.subtract') },
    { id: 'MULTIPLICATION', name: translate('automatisation.multiply') },
    { id: 'DIVISION', name: translate('automatisation.divide') },
    { id: 'MODULO', name: translate('automatisation.modulo') },
    { id: 'POWER', name: translate('automatisation.power') },
    { id: 'EQUALS', name: translate('automatisation.equals') }
   ];

   const string_operations = [
    { id: 'APPEND', name: translate('automatisation.append') },
    { id: 'PREPEND', name: translate('automatisation.prepend') },
    { id: 'EQUALS', name: translate('automatisation.equals') },
    { id: 'FIND_AND_REPLACE', name: translate('automatisation.find_and_replace') }
   ];

   const string_variable_operations = [
    { id: 'APPEND', name: translate('automatisation.append') },
    { id: 'PREPEND', name: translate('automatisation.prepend') },
    { id: 'EQUALS', name: translate('automatisation.equals') },
   ];


   const setOperation = (operation) => {
    setTmpStep({ ...tmpStep, data: { ...tmpStep.data, operation: operation.id } });
   }

   const setValueType = (valueType) => {
    if (valueType === 'variable') {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value_type: valueType, value: sourceOptions[0].id}});
    } else {
        setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value_type: valueType, value: valueType === 'number' ? 0 : '' } });
    }
   }

   const valueTypeOptions = [
    { id: 'number', name: translate('automatisation.number') },
    { id: 'string', name: translate('automatisation.string') },
    { id: 'string_variable', name: translate('automatisation.string_variable') },
    { id: 'number_variable', name: translate('automatisation.number_variable') }
   ];

   const valueType =  valueTypeOptions.find(valueType => valueType.id === tmpStep.data.value_type) || valueTypeOptions[0];

   const selectedValueSource = sourceOptions.find(source => tmpStep.data.value?.startsWith && tmpStep.data.value?.startsWith(source.id)) || sourceOptions[0];

   const selectedOperation = valueType.id === 'number' || valueType.id === 'number_variable' ? number_operations.find(operation => operation.id.toLowerCase() === tmpStep.data.operation.toLowerCase()) || number_operations[0] : 
    valueType.id === 'string_variable' ? string_variable_operations.find(operation => operation.id.toLowerCase() === tmpStep.data.operation.toLowerCase()) || string_variable_operations[0] : 
        string_operations.find(operation => operation.id.toLowerCase() === tmpStep.data.operation.toLowerCase()) || string_operations[0];

   const setValueSource = (source) => {
    setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value_type: valueType.id, value: source.id + tmpStep.data.value.split(selectedValueSource.id)[1] } });
   }

   const variableValue = valueType.id === 'number_variable' || valueType.id === 'string_variable' ? tmpStep.data.value.split(selectedValueSource.id + ".").slice(1).join(".") : tmpStep.data.value;

   const setVariableValue = (value) => {
    setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: selectedValueSource.id + "." + value } });
   }
  
    return (
        <div className="flex flex-col mt-4 overflow-visible">

            {error && <p className="text-red-600 flex flex-row items-center bg-red-50 p-4"><ExclamationCircleIcon className="w-6 h-6 mr-2" />{error}</p>}
            <p className="text-xl text-gray-600 font-bold mt-2">{translate('automatisation.variable')}</p>

            <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.from')}</p>
            <Select selected={selectedSource} options={sourceOptions} setSelected={(e) => setSource(e)} />

            <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.value')}</p>
            <TextInput value={variable} onChange={(e) => setVariable(e.target.value)} />
            
            <p className="text-lg text-gray-600 font-bold mt-2">{translate('automatisation.value_type')}</p>
            <div className="flex flex-col">
                <Select options={valueTypeOptions} selected={valueType} setSelected={(e) => setValueType(e.id)} name="valueType" />
            </div>

            <p className="text-lg text-gray-600 font-bold mt-2">{translate('automatisation.operation')}</p>
            <Select selected={selectedOperation} options={valueType.id === 'number' || valueType.id === 'number_variable' ? number_operations : valueType.id === 'string_variable' ? string_variable_operations : string_operations} setSelected={(e) => setOperation(e)} />

            {(valueType.id === 'string_variable' || valueType.id === 'number_variable') && selectedOperation?.id !== 'FIND_AND_REPLACE' && <div className="flex flex-col mt-2">
                <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.from')}</p>
                <Select selected={selectedValueSource} options={sourceOptions} setSelected={(e) => setValueSource(e)} />

                <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.value')}</p>
                <TextInput value={variableValue} onChange={(e) => setVariableValue(e.target.value)} />
            </div>}

            {(valueType.id === 'number' || valueType.id === 'string') && selectedOperation?.id !== 'FIND_AND_REPLACE' && <div className="flex flex-col mt-2">
                <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.value')}</p>
                <TextInput type={valueType.id === 'number' ? 'number' : 'text'} value={tmpStep.data.value} onChange={(e) => setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: e.target.value } })} />
            </div>}

            {selectedOperation.id === 'FIND_AND_REPLACE' && <div className="flex flex-col mt-2">
                <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.find')}</p>
                <TextInput value={tmpStep.data.value} onChange={(e) => setTmpStep({ ...tmpStep, data: { ...tmpStep.data, value: e.target.value } })} />
                <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.replace')}</p>
                <TextInput value={tmpStep.data.second_value} onChange={(e) => setTmpStep({ ...tmpStep, data: { ...tmpStep.data, second_value: e.target.value } })} />
            </div>}

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

const DescriptionTab = ({ tmpStep, setTmpStep, save, isCreate }) => {
    const { translate } = useTranslation();
    return (
        <div className="flex flex-col mt-4">
            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.description')}</p>
            <TextArea rows={5} value={tmpStep.description} onChange={(e) => setTmpStep({ ...tmpStep, description: e.target.value })} />

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep, JSON.stringify(tmpStep.data.body))}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

export default function UpdateVariablePanel({ setStepToClone, panelHook, step, refresh, deleteStep, isCreate, onCreateNewStep }) {
    const { translate } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [tmpStep, setTmpStep] = useState({ ...step });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const api = useAPI();

    const tabs = useTabs([
        { icon: Cog8ToothIcon, name: translate('automatisation.configuration'), current: true },
        { icon: DocumentIcon, name: translate('automatisation.description'), current: false }
    ]);
    
    // Reset tmpStep when panel is closed
    useEffect(() => {
        if (!panelHook.open) {
            setTmpStep({ ...step });
        }
    }, [panelHook.open]);

    const save = async (tmpStep) => {


        try {
            if (isCreate) {
                if (!tmpStep.description || tmpStep.description.trim() === '') {
                    const variableName = tmpStep.data.variable.split(".").slice(1).join(".");
                    const source = tmpStep.data.variable.split(".")[0];
                    tmpStep.description = "Update variable " + variableName + " from " + source;
                }

                const newStepResponse = await api.createAutomatisationStep(tmpStep.automatisation_id, tmpStep);
                if (newStepResponse.data.automatisation_step) {
                    onCreateNewStep(newStepResponse.data.automatisation_step);
                }
            } else {
                await api.updateAutomatisationStep(tmpStep.automatisation_id, tmpStep);
            }

            panelHook.setOpen(false);
            setTimeout(() => {
                refresh();
            }, 1000);
        } catch (e) {
            setError('Invalid JSON');
            return;
        }

    }

    const selectStepToClone = (step) => {
        setStepToClone(step);
        panelHook.setOpen(false);
    }


    return (
        <div className="flex flex-col flex-1 rounded-md p-4 overflow-hidden h-full w-96">
            <div className="flex flex-row items-center">
                <VariableIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md mr-2 ${textColor}`} />
                <div className="flex flex-col ml-2">
                    <h2 className="text-xl font-bold">{translate('automatisation.updateVariable')}</h2>
                </div>
                
                <div className="flex flex-row flex-1 justify-end">

                {!isCreate && <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={deleteStep}>
                        {translate('delete')}
                    </Button>}
                {isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => save(tmpStep)}>{translate('create')}</Button>}
                {!isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => selectStepToClone(tmpStep)}>{translate('clone')}</Button>}
                </div>
            </div>

            <div className="flex flex-row overflow-x-scroll no-scrollbar">
                <Tabs tabs={tabs} />
            </div>

            {tabs.currentTabIndex === 0 && <ConfigurationTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
            {tabs.currentTabIndex === 1 && <DescriptionTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
        </div>
    )
}